import { isEmpty, isNil } from "lodash"
import { createSelector } from "reselect"

import { DEFAULT_LANGUAGE, OPF_DOCUMENT_TYPES } from "src/globals"
import { getDocumentSuperType } from "src/components/helpers"
import { getCurrentLanguage } from "src/store/locale/selectors"
import { getCurrentTree } from "src/store/utils/routeUtils"
import { selectPathnameSlug } from "src/store/ui/selectors"
import { DocumentEditMode, OpfDocument } from "../../types"

const { POLICY, STANDARD, POLICIES_STANDARDS_CATEGORY, DOCUMENT_CATEGORY } = OPF_DOCUMENT_TYPES

export const getDocuments = (state: any) => state.documents
export const getDocumentsAvailableCategoryActions = (state: any) =>
  state.documents.availableCategoryActions
export const getAllDocumentsBySlug = (state: any) => state.documents.bySlug
export const getAllDocuments = createSelector(getAllDocumentsBySlug, (documentsBySlug) =>
  Object.values(documentsBySlug)
)
export const getAllChildDocuments = (state: any) => (opfDocument: any) =>
  opfDocument
    ? getAllDocuments(state).filter((child: any) => child.parentId === opfDocument.opfId)
    : []

export const getSelectedDocument = (state: any) => state.documents.selectedDocument

export const selectEditMode = (state: any): false | DocumentEditMode => state.documents.editMode

export const selectIsPreviewMode = createSelector(selectEditMode, (editMode) => !editMode)

export const selectIsEditMode = createSelector(
  selectEditMode,
  (editMode) =>
    editMode && [DocumentEditMode.EDIT_EN, DocumentEditMode.EDIT_NON_EN].includes(editMode)
)

export const selectIsTranslateMode = createSelector(
  selectEditMode,
  selectIsEditMode,
  (editMode, isEditMode) => editMode && !isEditMode
)

export const getFetchingCategoriesState = (state: any) => state.documents.fetchingCategories

export const getAllRelationCategories = createSelector(getAllDocuments, (relatedDocuments) =>
  relatedDocuments.filter((opfDocument: any) => opfDocument.type === POLICIES_STANDARDS_CATEGORY)
)

export const getAllRelatedChildDocuments = createSelector(
  getAllDocuments,
  (allRelatedDocuments) => (opfDocument: any) =>
    allRelatedDocuments.filter(({ parentId }: any) => parentId === opfDocument.opfId)
)

export const getRelatedDocumentById = createSelector(
  getAllDocuments,
  (allRelatedDocuments) => (opfId: any) =>
    allRelatedDocuments.find((opfDocument: any) => opfDocument.opfId === opfId)
)

export const getRelatedDocumentChildrenRecursively =
  (state: any): any =>
  (opfDocument: any): any => {
    const children = getAllRelatedChildDocuments(state)(opfDocument)
    if (!isEmpty(children)) {
      return [{ ...opfDocument }, ...children.flatMap(getRelatedDocumentChildrenRecursively(state))]
    }
    return [opfDocument]
  }

export const getAllRelatedChildPoliciesAndStandards = (state: any) => (opfDocument: any) => {
  const allChildren = getRelatedDocumentChildrenRecursively(state)(opfDocument) || []
  return allChildren.filter((childDocument: any) => [POLICY, STANDARD].includes(childDocument.type))
}

export const getDocumentsForCurrentTree = createSelector(
  getAllDocuments,
  getCurrentLanguage,
  (documentsArray, currentLanguage) => {
    const tree = getCurrentTree(currentLanguage)
    return documentsArray.filter((document: any) => document.tree === tree)
  }
)

export const getCategoriesForCurrentTree = createSelector(
  getAllDocuments,
  getCurrentLanguage,
  (documentsArray, currentLanguage) => {
    const tree = getCurrentTree(currentLanguage)
    const categoriesWithinTree = documentsArray.reduce((accumulated: any, document: any) => {
      const superType = getDocumentSuperType(document.type)
      if (document.tree === tree && superType === DOCUMENT_CATEGORY) {
        return { ...accumulated, [document.slug]: document }
      } else {
        return accumulated
      }
    }, {})
    return categoriesWithinTree
  }
)

export const getDocumentBySlug = (state: any) => (slug: string) => state.documents.bySlug[slug]

export const getAllItems = createSelector(
  (state: any) => state.documents.allItems,
  (state: any) => state.documents.bySlug,
  (allItems, bySlug) => allItems.map((slug: string) => bySlug[slug])
)

export const getItemById = createSelector(
  getAllItems,
  (allItems) => (opfId: string) =>
    allItems.filter((item: any) => !isNil(item)).find((item: any) => item.opfId === opfId)
)

export const getDocumentsForTree = (state: any) => (tree: any) =>
  Object.values(state.documents.bySlug).filter((document: any) => document.tree === tree)

export const isUploadingRteFile = createSelector(
  getDocuments,
  (documents) => documents.isUploadingRteFile
)

export const selectTranslatingDocument = createSelector(
  getDocuments,
  (documents): OpfDocument | undefined => documents.translatedDocument
)

export const selectIsTranslating = createSelector(
  getDocuments,
  (documents): boolean => documents.isTranslating
)

export const getDocumentVersions = (state: any) => state.documents.versions

export const selectDocumentVersionLeft = (state: any) => state.documents.leftDocument

export const selectDocumentVersionRight = (state: any) => state.documents.rightDocument

export const selectIsCompareMode = (state: any): boolean => state.documents.documentCompareMode

export const selectCurrentDocument = createSelector(
  getAllDocumentsBySlug,
  selectPathnameSlug,
  (documentBySlug: Record<string, OpfDocument>, slug): OpfDocument | undefined =>
    documentBySlug[slug]
)

export const selectCurrentDocumentOrFail = createSelector(
  selectCurrentDocument,
  (currentDocument): OpfDocument => {
    if (!currentDocument) {
      throw new ReferenceError("Current document is not available")
    }

    return currentDocument
  }
)

export const selectInitialDocument = (state: any): OpfDocument | undefined =>
  state.documents.initialDocument

export const selectCurrentDocumentEditAction = createSelector(
  selectCurrentDocument,
  (currentDocument): DocumentEditMode | false => {
    if (!currentDocument) {
      return false
    }

    return currentDocument.language === currentDocument.initialLanguage
      ? currentDocument.language === DEFAULT_LANGUAGE
        ? DocumentEditMode.EDIT_EN
        : DocumentEditMode.EDIT_NON_EN
      : DocumentEditMode.EDIT_TRANSLATION
  }
)

export const selectCurrentDocumentIsInitial = createSelector(
  selectCurrentDocument,
  (currentDocument) =>
    currentDocument ? currentDocument.language === currentDocument.initialLanguage : false
)
